/**
 * useRaMode — reports whether the backend's RA datalake adapter is currently
 * in 'fixture' or 'live' mode. Fetches `/api/analytics/_mode` once per session
 * and caches the result so it's free for every widget that asks.
 *
 * Used by FixtureBadge to auto-hide once James flips RA_DATALAKE_MODE=live.
 * No code change required at cutover.
 */

import { useEffect, useState } from 'react';
import { API_BASE_URL } from '@/utils/constants';
import { getDevHeaders } from '@/services/api';

type RaMode = 'fixture' | 'live' | 'unknown';

let cached: RaMode | null = null;
let inflight: Promise<RaMode> | null = null;

async function fetchRaMode(): Promise<RaMode> {
  if (cached) return cached;
  if (inflight) return inflight;
  inflight = fetch(`${API_BASE_URL}/api/analytics/_mode`, {
    credentials: 'include',
    headers: getDevHeaders(),
  })
    .then(async (res) => {
      if (!res.ok) return 'unknown' as RaMode;
      const json = (await res.json()) as { mode?: string };
      const mode = json.mode === 'live' || json.mode === 'fixture' ? (json.mode as RaMode) : 'unknown';
      cached = mode;
      return mode;
    })
    .catch(() => 'unknown' as RaMode)
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** Hook returning the current RA datalake mode + a loading flag. */
export function useRaMode(): { mode: RaMode; loading: boolean } {
  const [mode, setMode] = useState<RaMode>(cached ?? 'unknown');
  const [loading, setLoading] = useState<boolean>(cached === null);

  useEffect(() => {
    if (cached !== null) {
      setMode(cached);
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetchRaMode().then((m) => {
      if (!cancelled) {
        setMode(m);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return { mode, loading };
}

/** Test-only / James-only: clear the cache so the next caller refetches. */
export function __resetRaModeCache(): void {
  cached = null;
  inflight = null;
}
